import type { HeaderFilterGroup } from 'shared/components/filter-header'
import type { FilterableItemType, FilterDataInterface } from 'shared/components/listing-layout'
import {
  toFilterGroup,
  useFilterDraft,
  type FilterControl,
  type FilterDraftState,
} from 'shared/hooks/use-filter-draft'
import { OrderEnum } from 'shared/types/pagination-interface'

const isVisible = <K>(field: FilterableItemType<K>, values: K) =>
  (typeof field.shouldRender === 'function' ? field.shouldRender(values) : field.shouldRender) !==
  false

/** Adapts a module's `filterData` descriptors to the header's group contract. */
export const useListingFilter = <K>(filterData: FilterDataInterface<K>): HeaderFilterGroup => {
  const { filterable, filterer } = filterData
  const state = useFilterDraft<K>({
    applied: filterer.filter,
    defaults: filterer.defaultFilters,
    onApply: values => {
      filterData.setPagination?.({ startFromId: undefined, order: OrderEnum.next })
      filterer.setFilter(values)
    },
    unavailableFields: values => getUnavailableFilterFields(filterable, values),
    ready: filterData.ready,
  })

  return toListingFilterGroup(state, filterable)
}

export const getUnavailableFilterFields = <K>(filterable: FilterableItemType<K>[], values: K) =>
  filterable
    .filter(field => !isVisible(field, values))
    .flatMap(field => field.fields ?? [field.field])

/** Render descriptors against a module-owned draft, including dependent option data. */
export const toListingFilterGroup = <K>(
  state: FilterDraftState<K>,
  filterable: FilterableItemType<K>[],
) => {
  const controls: FilterControl<K>[] = filterable
    .filter(field => isVisible(field, state.draft))
    .map(field => ({
      key: String(field.field),
      fields: field.fields,
      node: field.onRender(
        state.draft[field.field],
        value =>
          state.setDraft(previous => {
            const next: K = { ...previous, [field.field]: value }
            const defaultValue = state.defaults[field.field]
            if (value === '' || value === null || value === undefined)
              next[field.field] =
                defaultValue === '' || defaultValue === null || defaultValue === undefined
                  ? defaultValue
                  : value
            return next
          }),
        state.draft,
        state.setDraft,
      ),
    }))

  return toFilterGroup(state, controls)
}
